import { Stack } from "@mui/material";
import { Grid } from "@mui/system";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { SectionCard } from "routes/Customer/Waste/Manifests/sections/SectionCard";
import { SectionHeader } from "routes/Customer/Waste/Manifests/sections/SectionHeader";

import { FieldGroupHeading } from "./FieldGroupHeading";
import { WasteClassificationExplanation } from "./types";

type InputRow = { label: string; text: string; fullWidth?: boolean };

/** Codes joined for display, or "None" when the line carries none. */
function codesText(codes: readonly string[]): string {
  return codes.length > 0 ? codes.join(", ") : "None";
}

function lineRows(inputs: WasteClassificationExplanation["inputs"]): InputRow[] {
  const rows: InputRow[] = [
    { label: "Federal waste codes", text: codesText(inputs.federalCodes) },
    { label: "State waste codes", text: codesText(inputs.stateCodes) },
    { label: "Description", text: inputs.description ?? "None", fullWidth: true },
    { label: "Management method code", text: inputs.managementMethodCode ?? "None" },
  ];
  if (inputs.finalManagementMethodCode != null) {
    rows.push({ label: "Final management method code", text: inputs.finalManagementMethodCode });
  }
  return rows;
}

function facilityRows(facility: WasteClassificationExplanation["facilityContext"]): InputRow[] {
  const rows: InputRow[] = [
    { label: "Facility state", text: facility.state },
    { label: "Subpart P", text: facility.subpartP ? "Yes" : "No" },
  ];
  if (facility.txSteersGeneratorType) {
    rows.push({ label: "TX STEERS generator type", text: facility.txSteersGeneratorType });
  }
  return rows;
}

function InputGrid({ rows, heading }: { rows: readonly InputRow[]; heading?: string }) {
  return (
    <Grid container spacing={2}>
      {heading && (
        <Grid size={{ xs: 12 }}>
          <FieldGroupHeading>{heading}</FieldGroupHeading>
        </Grid>
      )}
      {rows.map((row) => (
        <Grid key={row.label} size={row.fullWidth ? { xs: 12 } : { xs: 12, sm: 6 }}>
          <DisplayOnlyField label={row.label} text={row.text} copyable={false} />
        </Grid>
      ))}
    </Grid>
  );
}

/**
 * The line facts and facility facts the rules read, after the engine's own
 * source selection — not necessarily what's stored on the line, since the
 * engine already picked among vendor, eManifest and reviewed document
 * sources for `inputs`.
 */
export function WasteClassificationInputs({
  explanation,
}: {
  explanation: Pick<WasteClassificationExplanation, "inputs" | "facilityContext">;
}) {
  return (
    <SectionCard>
      <SectionHeader title="What Encamp read" />
      <Stack gap={2}>
        <InputGrid rows={lineRows(explanation.inputs)} />
        <InputGrid rows={facilityRows(explanation.facilityContext)} heading="Facility" />
      </Stack>
    </SectionCard>
  );
}
